import { Alert, Box } from '@mui/material';
import UPButton from 'components/button';
import { toNumber } from 'lodash';
import { CloseAlertIcon } from 'modules/companies/styles';
import { formatTotalHours, parseHours, validateTotalHours } from 'modules/payroll-settings/service-codes/helpers';
import WorkScheduleFields from 'modules/payroll-settings/service-codes/WorkScheduleFields';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import {
  getFixedEmployeesWorkhourSettingsAction,
  updateFixedEmployeesWorkhourSettingsAction,
  WageCalculationSaveResult,
} from 'store/actions/wage-calculation-actions';
import { fixedEmployeesWorkhourSettingsSelector } from 'store/selectors/wage-calculation-selectors';
import {
  COMPANY_INFORMATION__COMPANY_UPDATED_SUCCESSFULLY,
  GENERAL__CANCEL_BUTTON,
  GENERAL__SAVE_BUTTON,
} from 'translations/constants';
import { IFixedEmployeesWorkhourSettings } from 'types/wage-calculation';
import { ReactComponent as CloseIcon } from '../../../assets/icons/Close.svg';
import { PayrollTabAlerts, PayrollTabContainer } from './styles';

// the company's default work schedule: not linked to a joint commission, only used by time registration
const PayrollFixedEmployment = () => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { companyId } = useParams();

  const fixedEmployeesWorkhourSettingsFromRedux = useAppSelector(fixedEmployeesWorkhourSettingsSelector);

  const [fixedEmployeesWorkhourSettings, setFixedEmployeesWorkhourSettings] =
    useState<IFixedEmployeesWorkhourSettings>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveResult, setSaveResult] = useState<WageCalculationSaveResult | undefined>(undefined);

  const { isValid } = validateTotalHours(fixedEmployeesWorkhourSettings?.totalHours, true);

  useEffect(() => {
    if (companyId) {
      dispatch(getFixedEmployeesWorkhourSettingsAction(toNumber(companyId)));
    }
  }, [dispatch, companyId]);

  useEffect(() => {
    if (fixedEmployeesWorkhourSettingsFromRedux) {
      setFixedEmployeesWorkhourSettings({
        ...fixedEmployeesWorkhourSettingsFromRedux,
        totalHours: formatTotalHours(fixedEmployeesWorkhourSettingsFromRedux.totalHours),
      });
    }
  }, [fixedEmployeesWorkhourSettingsFromRedux]);

  const onSave = async () => {
    setIsSaving(true);
    setSaveResult(undefined);
    const result = await dispatch(
      updateFixedEmployeesWorkhourSettingsAction({
        companyId: toNumber(companyId),
        fixedEmployeesWorkhourSettings: {
          ...fixedEmployeesWorkhourSettings,
          totalHours: parseHours(fixedEmployeesWorkhourSettings?.totalHours),
        },
      }),
    ).unwrap();
    setSaveResult(result);
    setIsSaving(false);
  };

  const onCancel = () => {
    navigate(`/company/${companyId}/general`);
  };

  return (
    <PayrollTabContainer>
      <WorkScheduleFields
        workingSystem={fixedEmployeesWorkhourSettings?.workingSystem ?? null}
        totalHours={fixedEmployeesWorkhourSettings?.totalHours ?? null}
        allowEmptyTotalHours
        stacked
        onChangeWorkingSystem={workingSystem =>
          setFixedEmployeesWorkhourSettings(prevState => ({ ...prevState, workingSystem }))
        }
        onChangeTotalHours={totalHours =>
          setFixedEmployeesWorkhourSettings(prevState => ({ ...prevState, totalHours }))
        }
      />
      <Box sx={{ marginTop: '32px', display: 'flex', flexDirection: 'row', flexWrap: 'wrap' }}>
        <Box sx={{ marginRight: '16px' }}>
          <UPButton text={t(GENERAL__SAVE_BUTTON)} disabled={!isValid || isSaving} onClick={onSave} />
        </Box>
        <UPButton text={t(GENERAL__CANCEL_BUTTON)} onClick={onCancel} outlined />
      </Box>
      <PayrollTabAlerts>
        {saveResult === null && (
          <Alert
            severity="success"
            icon={false}
            variant="filled"
            sx={{ width: '800px' }}
            action={
              <CloseAlertIcon
                onClick={event => {
                  event.preventDefault();
                  event.stopPropagation();
                  setSaveResult(undefined);
                }}
              >
                <CloseIcon fill="white" />
              </CloseAlertIcon>
            }
          >
            {t(COMPANY_INFORMATION__COMPANY_UPDATED_SUCCESSFULLY)}
          </Alert>
        )}
        {!!saveResult && (
          <Alert severity="error" icon={false} variant="filled" sx={{ width: '800px' }}>
            {t(saveResult)}
          </Alert>
        )}
      </PayrollTabAlerts>
    </PayrollTabContainer>
  );
};

export default PayrollFixedEmployment;
